/* eslint-disable @typescript-eslint/no-explicit-any */
import * as FileSaver from 'file-saver';
import { message } from 'antd';
import * as XLSX from 'xlsx';

export const formatNumber = (num: number | string | null) => {
  if (num !== undefined) {
    return parseFloat(Number(num).toFixed(2))
      .toString()
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  }
  return '0';
};

export const formatCurrency = (value: number, currency?: string) =>
  new Intl.NumberFormat('en-'.concat(currency?.slice(0, 2) || 'NG'), {
    style: 'currency',
    currency: currency || 'NGN',
  })
    .format(value)
    .replace('.00', '');

export const handleDataExport = (name: string, parseData: any) => {
  const ws = XLSX.utils.json_to_sheet(parseData);
  const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });
  FileSaver.saveAs(data, `estateplus-${name}.xlsx`);
};

export const handleDataReduce = (data: any, key: string) => {
  return data?.reduce((a: any, b: any) => Number(a) + Number(b?.[key] || b), 0);
};

export const handleObj = (data: any) =>
  Object.fromEntries(Object.entries(data || {})?.filter(([, val]) => val));

export const handleExtObj = (data: any, keys: any[]) =>
  Object.fromEntries(Object.entries(data || {})?.filter(([key]) => keys?.includes(key)));

export const handleCopy = async (content: string) => await navigator.clipboard.writeText(content)
  .then(() => message.success('Copied to clipboard!'))
  .catch((err) => message.error(err || 'Copy failed'));

export const isEqual = (data: any, key: any) => data === key;

export const handleEmailHash = (data: string) => {
  const mailName = (data || '')?.slice(0, (data || '')?.indexOf('@'));
  const mailProvider = (data || '')?.slice((data || '')?.indexOf('@'));
  const mailNameCut = mailName.slice(0, Math.floor(mailName.length / 2));
  return mailNameCut.padEnd(mailName.length, '*').concat(mailProvider) || '';
};

export const handleCapitalize = (data: string) => (data || '').charAt(0).toUpperCase() + (data || '').slice(1);

export function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });
}

export function getBinary(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const data = (reader?.result as any)?.split(',')[1];
      resolve(atob(data));
    };
    reader.onerror = (err) => reject(err);
  });
}

export const checkOddValue = (data: string) => ['undefined', 'null'].includes(data);

export const statusType = {
  success: { col: '!text-[#12B76A]', bg: '!bg-[#ECFDF3]' },
  pending: { col: '!text-[#FFBD00]', bg: '!bg-[#FFBD001F]' },
  error: { col: '!text-[#E01020]', bg: '!bg-[#E010201F]' },
  info: { col: '!text-[#495057]', bg: '!bg-[#878A991F]' },
};

export const handleTableSelect = (data: any, setData: any, key: string) => ({
  type: 'checkbox',
  selectedRowKeys: data,
  onChange: (e: any, selectedRows: any) => {
    const arr: any[] = [];
    selectedRows.forEach((row: any) => arr.push(row?.[key] || row));
    setData(arr);
  },
});

export function handleAPIMerge(url: string, path: string, other?: boolean) {
  if (other) return path;
  const newPath = path?.slice(1);
  const newUrl = url?.slice(0, -1);
  const isUrlSl = url?.slice(-1) === '/';
  const mainUrl = isUrlSl ? newUrl : url;
  const isPathSl = path?.slice(0, 1) === '/';
  const mainPath = isPathSl ? newPath : path;
  return mainUrl?.concat('/', mainPath);
}

export const handleArray = (data: any[], other?: any) => Array.isArray(data || []) && (data || [])?.length ? data : other || [];

export const validatePassword = (pass: string) => {
  const criteria = [
    { regex: /.{8,}/, description: 'At least 8 characters' },
    { regex: /[A-Z]/, description: 'At least one uppercase letter' },
    { regex: /[0-9]/, description: 'At least one number' },
    { regex: /[!@#$^&*()]/, description: 'At least one special character' },
  ];

  return criteria.map((criterion) => ({
    ...criterion,
    isValid: criterion.regex.test(pass),
  }));
};
